import { Clusterer, Map, Placemark, YMaps } from '@pbe/react-yandex-maps'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ShopItem } from '@features/shipping-methods/types'

import { useShopsInGeoPolygonLazyQuery } from '@shared/generated'

import styles from './styles.module.scss'



const DEFAULT_CENTER: [number, number] = [ 50.300377, 57.154555 ]

export default function MapPickup ({
  center,
  onShopsDataUpdate,
  onShopClick,
}: {
  center?: [number, number] | null;
  onShopsDataUpdate: (data: ShopItem[]) => void;
  onShopClick?: (shop: ShopItem) => void;
}) {
  const mapRef = useRef<ymaps.Map | null>(null)
  const [ isMapReady, setMapReady ] = useState(false)
  const [ mapCenter ] = useState<[number, number]>(DEFAULT_CENTER)
  const [ fetchShops, { data } ] = useShopsInGeoPolygonLazyQuery()

  const filteredShops = useMemo(() => {
    return (
      data?.shopsInGeoPolygon?.filter(
        (shop) => shop?.isPickupDeliveryAvailable,
      ) || []
    )
  }, [ data?.shopsInGeoPolygon ])

  useEffect(() => {
    onShopsDataUpdate(filteredShops as ShopItem[])
  }, [ filteredShops, data?.shopsInGeoPolygon, onShopsDataUpdate ])

  useEffect(() => {
    if (center && isMapReady && mapRef.current) {
      mapRef.current.setCenter(center, 14, {
        duration: 300,
      })
    }
  }, [ center, isMapReady ])

  const getCornersFromBounds = useCallback((bounds: number[][]) => {
    const [ southWest, northEast ] = bounds
    const southEast = [ southWest[0], northEast[1] ]
    const northWest = [ northEast[0], southWest[1] ]

    return [
      { latitude: southWest[0], longitude: southWest[1] },
      { latitude: southEast[0], longitude: southEast[1] },
      { latitude: northEast[0], longitude: northEast[1] },
      { latitude: northWest[0], longitude: northWest[1] },
    ]
  }, [])

  // Включить для определения точного местоположения

  // useEffect(() => {
  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const { latitude, longitude } = position.coords;
  //       setMapCenter([latitude, longitude]);
  //     },
  //     () => {
  //       setMapCenter(DEFAULT_CENTER);
  //     },
  //   );
  // }, []);

  const handleBoundsChange = useCallback(() => {
    const mapInstance = mapRef.current
    if (!mapInstance) return

    const bounds = mapInstance.getBounds()
    if (!bounds) return

    const corners = getCornersFromBounds(bounds)
    fetchShops({ variables: { corners } })
  }, [ fetchShops, getCornersFromBounds ])

  useEffect(() => {
    const mapInstance = mapRef.current
    if (!isMapReady || !mapInstance) return

    const bounds = mapInstance.getBounds()
    if (bounds) {
      const corners = getCornersFromBounds(bounds)
      fetchShops({ variables: { corners } })
    }

    mapInstance.events.add('boundschange', handleBoundsChange)

    return () => {
      mapInstance.events.remove('boundschange', handleBoundsChange)
    }
  }, [ isMapReady, handleBoundsChange, fetchShops, getCornersFromBounds ])

  return (
    <div className={styles.map}>
      <YMaps
        query={{
          apikey: import.meta.env.VITE_YANDEX_MAPS_API_KEY,
          load: 'package.full',
        }}
      >
        <Map
          defaultState={{
            center: mapCenter,
            zoom: 4,
            controls: [ 'zoomControl' ],
          }}
          width="100%"
          height="100%"
          instanceRef={(ref) => {
            if (ref) {
              mapRef.current = ref
              setMapReady(true)
            }
          }}
          options={{
            suppressMapOpenBlock: true,
            copyrightLogoVisible: false,
            copyrightUaVisible: false,
          }}
        >
          <Clusterer
            options={{
              preset: 'islands#invertedRedClusterIcons',
              groupByCoordinates: false,
              clusterDisableClickZoom: false,
              clusterOpenBalloonOnClick: false,
            }}
          >
            {filteredShops?.map((shop) => {
              const latitude = shop?.shopGeoPoint?.latitude
              const longitude = shop?.shopGeoPoint?.longitude
              if (latitude == null || longitude == null) return null

              return (
                <Placemark
                  onClick={() => {
                    if (onShopClick) onShopClick(shop as ShopItem)
                  }}
                  key={shop?.id}
                  geometry={[ latitude, longitude ]}
                  options={{
                    iconLayout: 'default#image',
                    iconImageHref: '/icons/location_on.svg',
                    iconImageSize: [ 36, 56 ],
                    iconImageOffset: [ -16, -32 ],
                  }}
                />
              )
            })}
          </Clusterer>
        </Map>
      </YMaps>
    </div>
  )
}
